import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The admin type system defines 8 custom text-role utilities via
 * `@theme --text-*` in globals.css (see docs/patterns/admin-design-system.md).
 * Each sets font-size + line-height + font-weight in a single class:
 *   text-page-title text-page-sub text-section-heading text-card-title
 *   text-body text-caption text-label text-stat
 *
 * Out of the box tailwind-merge does not know these utilities, so it
 * misclassifies them as text-COLOR utilities. That silently strips a role
 * class whenever it is merged (via `cn`) alongside a colour class inside a ui
 * primitive — e.g. passing `text-label text-foreground` into a shadcn
 * `TableHead` (which bakes in `font-medium text-muted-foreground`) dropped the
 * role entirely, and a passed `text-body` lost to `NativeSelect`'s baked-in
 * `text-sm`.
 *
 * Teach tailwind-merge that the 8 role utilities are their own group so a role:
 *  - SURVIVES when merged next to a colour class (`text-foreground` etc.);
 *  - CONFLICTS with `font-size` (so `text-body` beats/loses-to `text-sm` by
 *    order) and with `font-weight` (so a primitive's baked-in `font-medium` is
 *    stripped once a role — which already sets its own weight — is present);
 *  - conflicts with itself (one role per element).
 * The relationship is registered in both orderings so it holds regardless of
 * which class is passed last.
 */
const twMerge = extendTailwindMerge<"textRole">({
	extend: {
		classGroups: {
			textRole: [
				{
					text: [
						"page-title",
						"page-sub",
						"section-heading",
						"card-title",
						"body",
						"caption",
						"label",
						"stat",
						"hero-stat"
					]
				}
			]
		},
		conflictingClassGroups: {
			textRole: ["font-size", "font-weight"],
			"font-size": ["textRole"],
			"font-weight": ["textRole"]
		}
	}
});

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}
